"use client";

import { IconArrowRight } from "@tabler/icons-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Loader } from "@/components/common/loader";
import { buttonVariants } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { MultidietPickerForm } from "@/features/multidiet-picker/components/multidiet-picker-form";
import { MultidietPickerFormMobile } from "@/features/multidiet-picker/components/multidiet-picker-form-mobile";
import { MultidietVariants } from "@/features/multidiet-picker/components/multidiet-variants";
import { useMultidietPickerForm } from "@/features/multidiet-picker/hooks/use-multidiet-picker-form";
import useMultidietSetupStore from "@/features/multidiet-picker/store/multidiet-picker.store";
import type { MultidietPopupType } from "@/features/multidiet-picker/types/multidiet-popup.type";
import type { MultidietPickerType } from "@/features/static-builder/types/multidiet-picker.type";
import { interpretColors } from "@/lib/helpers/typography";
import { useMobile } from "@/lib/hooks/use-mobile";
import { cn } from "@/lib/utils";

export const MultidietPicker = ({
  data,
  layout = "page",
}: {
  data: MultidietPickerType | MultidietPopupType;
  layout: "popup" | "page";
}) => {
  const t = useTranslations();
  const { form, onSubmit } = useMultidietPickerForm();
  const { isMobileExtendedPlus } = useMobile();
  const { multidietSetup, multidietSetupLabels } = useMultidietSetupStore();
  return (
    <section
      className={cn("py-0", layout === "popup" && "py-0")}
      id="multidiet-picker"
    >
      <div className="container">
        <div
          className={cn(
            "flex flex-col items-center gap-4",
            layout === "page" &&
              "rounded-xl bg-background-surface-secondary lg:flex-row lg:gap-16 lg:px-10 lg:py-14",
            layout === "popup" && "lg:p-6",
          )}
        >
          <header
            className={cn(
              "shrink-0 p-4 pb-0",
              layout === "popup" && "pt-8 lg:w-full lg:pt-0",
              layout === "page" && "pt-11 lg:w-[300px] lg:p-0 xl:w-[350px]",
            )}
          >
            <h2
              className={cn(
                "text-center font-medium text-4.5xl max-md:leading-10",
                layout === "page" && "lg:text-left",
              )}
              dangerouslySetInnerHTML={{
                __html: interpretColors(data.title || ""),
              }}
            />
            <p
              className={cn(
                "mb-2 text-center font-medium text-xl",
                layout === "page" && "lg:text-left",
                layout === "popup" && "hidden",
              )}
            >
              {data.subheading}
            </p>
            <p
              className={cn(
                "mb-2 text-center",
                layout === "popup" && "font-medium text-xl",
                layout === "page" &&
                  "hidden border-brand-green1/30 text-base lg:my-12 lg:block lg:border-b lg:pb-6 lg:text-left",
              )}
            >
              {data.description}
            </p>
            {data.buttonUrl && (
              <p
                className={cn(
                  "hidden lg:block",
                  layout === "popup" && "lg:hidden",
                )}
              >
                <a
                  className={cn(
                    buttonVariants({ variant: "default", size: "lg" }),
                    "h-14 font-medium",
                  )}
                  href={data.buttonUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  {t("multidiet-picker.see-more")}
                  <IconArrowRight className="ml-2" stroke={1.25} />
                </a>
              </p>
            )}
          </header>
          <div className="flex w-full flex-col gap-5 pb-11 max-lg:overflow-hidden lg:p-0">
            {multidietSetupLabels && multidietSetup ? (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div
                    className={cn(
                      layout === "popup" ? "mb-10" : "mb-8 xl:mb-14",
                    )}
                  >
                    {!isMobileExtendedPlus && multidietSetup ? (
                      <MultidietPickerForm
                        form={form}
                        layout={layout}
                        multidietSetup={multidietSetup}
                      />
                    ) : (
                      <MultidietPickerFormMobile form={form} layout={layout} />
                    )}
                  </div>
                  {multidietSetupLabels && (
                    <MultidietVariants
                      layout={layout}
                      multidietSetupLabels={multidietSetupLabels}
                    />
                  )}
                </form>
              </Form>
            ) : (
              <Loader />
            )}

            <div className="px-4 lg:px-0">
              <p className="text-center text-sm">
                {t("multidiet-picker.info")}
              </p>
              {data.buttonUrl && layout === "popup" && (
                <p className="mt-8 text-center">
                  <Link
                    className="flex items-center justify-center font-bold underline underline-offset-4 hover:no-underline"
                    href={data.buttonUrl}
                  >
                    {t("multidiet-picker.learn-more")}
                    <IconArrowRight className="ml-2 w-5" stroke={2} />
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
